// =============================================================================
// domain/dayAccounts.ts — the Day Overview's account-shaped half.
// -----------------------------------------------------------------------------
// Everything `domain/dayReport.ts` deliberately excludes. Beta draws the same
// line and says why: the scorecard, the curve and the strip are pure functions
// of a trade array and are SHARED with the Practice tab, while the prop rails,
// the by-account table and the plan recap are calendar- and account-shaped and
// "none of it means anything for a replay session".
//
// ── 🚨 THE BY-ACCOUNT TABLE IGNORES THE GLOBAL ACCOUNT FILTER ──
//
// Every other surface in this client is scoped by it. This one is not, and beta
// is explicit that the reason is transparency: it lists EVERY active account
// including the ones that did not trade that day, which read `N/A` across.
// Absence is shown rather than hidden — a day where one account sat out is a
// fact about the day, and filtering those rows away would make a five-account
// trader's table silently shrink to the ones that happened to be busy.
// =============================================================================

import { effectiveTradeDate, type DatedTrade } from './session';
import { tradeValue, type ContractSpec } from './trade';
import type { DayBoundaryMode, PnlUnit } from './types';

// ---------------------------------------------------------------------------
// Days traded
// ---------------------------------------------------------------------------

/**
 * Distinct days this account traded, within the pool it is given.
 *
 * ⚠ THE POOL IS THE CALLER'S JOB, and it must be the ATTEMPT, not all history.
 * Beta scopes it with `tradesInAttempt`, and the reason is the same one recorded
 * against the profit-target rail: an account that was reset carries days from a
 * previous attempt that the firm no longer counts, so an un-scoped total says a
 * payout gate is met when it is not. This function does not guess — it counts
 * what it is handed.
 *
 * ⚠ A SCRATCH IS A TRADING DAY. `netPnl` is not read at all here: prop firms
 * count the day you took a trade, not the day you made money on one.
 */
export function daysTraded(
  pool: readonly DatedTrade[],
  mode: DayBoundaryMode,
): number {
  const days = new Set<string>();
  for (const t of pool) {
    const d = effectiveTradeDate(t, mode);
    if (d) days.add(d);
  }
  return days.size;
}

// ---------------------------------------------------------------------------
// The by-account table
// ---------------------------------------------------------------------------

export interface DayAccountTrade extends DatedTrade {
  accountId: string | null;
  contract: string | null;
  qty: number | null;
  points: number | null;
  netPnl: number | null;
  fees: number | null;
  rrRealized: number | null;
  riskPoints: number | null;
}

export interface DayAccountRow {
  accountId: string;
  name: string;
  /** False when the account has no trades this day — the row renders `N/A`. */
  traded: boolean;
  /** Cumulative progress toward the account's profit target, or null. */
  progressPct: number | null;
  /** Day P&L in the DISPLAY unit. */
  pnl: number;
  /** Day commissions, always in dollars. */
  fees: number;
  /** Trade count. */
  n: number;
  /** 0–100, or null when every trade was a scratch. */
  winRate: number | null;
  /** `Infinity` when there were wins and no losses. */
  profitFactor: number;
}

/**
 * One row per ACTIVE account — traded first, then by day P&L descending.
 *
 * ── 🚨 WIN RATE AND PROFIT FACTOR ARE COMPUTED IN DOLLARS, ALWAYS ──
 *
 * Only `pnl` follows the display unit. Beta reads `netPnl` for the win/loss
 * split and for the gross-won / gross-lost ratio regardless of what the user is
 * looking at, and that is correct rather than an oversight: a profit factor
 * built from points would compare an ES point against an MNQ point and call the
 * result a ratio. The same reason `pnlAgg` refuses to sum points across roots.
 *
 * ⚠ THE WIN-RATE DENOMINATOR EXCLUDES SCRATCHES. `decided = wins + losses`, so a
 * day of three wins, one loss and two flats reads 75%, not 50%. Dividing by the
 * trade count instead would make a scratch look like a loss.
 */
export function dayAccountRows(
  accounts: readonly { id: string; name: string }[],
  dayTrades: readonly DayAccountTrade[],
  unit: PnlUnit,
  catalogue: readonly ContractSpec[],
  progressFor: (accountId: string) => number | null,
): DayAccountRow[] {
  const byAccount = new Map<string, DayAccountTrade[]>();
  for (const t of dayTrades) {
    const id = t.accountId == null ? '' : String(t.accountId);
    if (!id) continue;
    const list = byAccount.get(id);
    if (list) list.push(t); else byAccount.set(id, [t]);
  }

  const rows = accounts.map((a): DayAccountRow => {
    const id = String(a.id);
    const base = {
      accountId: id,
      name: a.name || 'Account',
      progressPct: progressFor(id),
    };
    const ts = byAccount.get(id) ?? [];
    if (!ts.length) {
      return { ...base, traded: false, pnl: 0, fees: 0, n: 0, winRate: null, profitFactor: 0 };
    }

    let pnl = 0, fees = 0, wins = 0, losses = 0, grossWon = 0, grossLost = 0;
    for (const t of ts) {
      const v = tradeValue(t, unit, catalogue);
      if (v != null) pnl += v;
      fees += Number(t.fees) || 0;
      const np = Number(t.netPnl) || 0;
      if (np > 0) { wins++; grossWon += np; } else if (np < 0) { losses++; grossLost += -np; }
    }
    const decided = wins + losses;
    return {
      ...base,
      traded: true,
      pnl, fees, n: ts.length,
      winRate: decided ? (100 * wins) / decided : null,
      // ⚠ `Infinity` rather than a large number or null — the table renders `∞`,
      // which is the honest answer for a day with no losing trade. A null would
      // render an em dash and read as "not computed".
      profitFactor: grossLost > 0 ? grossWon / grossLost : (grossWon > 0 ? Infinity : 0),
    };
  });

  // ⚠ Traded rows first, then by P&L descending. Untraded rows keep the
  // account order they arrived in — sorting them by a P&L of 0 would shuffle
  // them arbitrarily on every render.
  return rows.sort((x, y) => {
    if (x.traded !== y.traded) return x.traded ? -1 : 1;
    return x.traded ? y.pnl - x.pnl : 0;
  });
}

// ---------------------------------------------------------------------------
// The "vs your average day" baseline
// ---------------------------------------------------------------------------

export interface BaselineTrade extends DatedTrade {
  contract: string | null;
  qty: number | null;
  points: number | null;
  netPnl: number | null;
  rrRealized: number | null;
  riskPoints: number | null;
}

/**
 * Mean P&L per TRADING DAY across the whole book, in the display unit.
 *
 * ── ⚠ THE DENOMINATOR IS DAYS TRADED, NOT DAYS ELAPSED ──
 *
 * Beta divides total P&L by the number of DISTINCT dates that carry a trade. A
 * calendar-day denominator would halve the figure for anyone who trades three
 * days a week, and the label says "avg day" meaning "a day you traded".
 *
 * ⚠ Returns null on an empty book rather than 0. Zero is a real average — a
 * breakeven trader — and rendering it for a user with no history would put
 * "avg day $0" under their very first day.
 */
export function averageDayPnl(
  book: readonly BaselineTrade[],
  unit: PnlUnit,
  catalogue: readonly ContractSpec[],
  mode: DayBoundaryMode,
): number | null {
  const days = new Set<string>();
  let total = 0;
  for (const t of book) {
    const d = effectiveTradeDate(t, mode);
    if (d) days.add(d);
    const v = tradeValue(t, unit, catalogue);
    if (v != null) total += v;
  }
  if (!days.size) return null;
  return total / days.size;
}
